import { isDefined, toError } from "@clipboard-health/util-ts";
import { Capacitor } from "@capacitor/core";
import type {
  DownloadBundleProgressEvent,
  NextBundleSetEvent,
} from "@capawesome/capacitor-live-update";
import { APP_V2_APP_EVENTS, logError, logEvent } from "@src/appV2/lib/analytics";
import { Platform } from "@src/mobile/utils/platform";

export interface LiveUpdateReadyResult {
  currentBundleId: string | null;
  previousBundleId: string | null;
  rollback: boolean;
}

export interface LiveUpdateDownloadLifecycleObserver {
  onDownloadProgress?: (event: DownloadBundleProgressEvent) => void;
  onNextBundleSet?: (event: NextBundleSetEvent) => void;
}

export interface LiveUpdateDownloadLifecycleSubscription {
  remove: () => Promise<void>;
}

/**
 * Signals to the Live Update plugin that the app has loaded successfully.
 * This prevents automatic rollback when readyTimeout is configured.
 * Logs the result to Datadog. On web, this is a no-op.
 */
export async function signalLiveUpdateReady(): Promise<LiveUpdateReadyResult> {
  if (!Platform.isMobile) {
    return { currentBundleId: null, previousBundleId: null, rollback: false };
  }

  const { LiveUpdate } = await import("@capawesome/capacitor-live-update");
  const result = await LiveUpdate.ready();

  if (result.rollback) {
    logEvent(APP_V2_APP_EVENTS.LIVE_UPDATE_ROLLBACK, {
      currentBundleId: result.currentBundleId,
      previousBundleId: result.previousBundleId,
    });
  } else {
    logEvent(APP_V2_APP_EVENTS.LIVE_UPDATE_READY, {
      currentBundleId: result.currentBundleId,
    });
  }

  return result;
}

/**
 * Returns the marketing version of the installed native binary (e.g. "3.7.2").
 * Returns null on web.
 */
export async function getNativeVersionName(): Promise<string | null> {
  if (!Platform.isMobile) {
    return null;
  }

  const { LiveUpdate } = await import("@capawesome/capacitor-live-update");
  const { versionName } = await LiveUpdate.getVersionName();
  return versionName;
}

/**
 * Returns the currently active live update bundle ID.
 * Returns null if running the default built-in bundle (no OTA applied), on web, or on error.
 */
export async function getCurrentBundleId(): Promise<string | null> {
  if (!Platform.isMobile) {
    return null;
  }

  try {
    const { LiveUpdate } = await import("@capawesome/capacitor-live-update");
    const result = await LiveUpdate.getCurrentBundle();
    return result.bundleId ?? null;
  } catch (error: unknown) {
    logError(APP_V2_APP_EVENTS.LIVE_UPDATE_READY_FAILURE, { error });
    return null;
  }
}

export async function observeLiveUpdateDownloadLifecycle({
  onDownloadProgress,
  onNextBundleSet,
}: LiveUpdateDownloadLifecycleObserver): Promise<
  LiveUpdateDownloadLifecycleSubscription | undefined
> {
  if (!Platform.isMobile || !Capacitor.isPluginAvailable("LiveUpdate")) {
    return undefined;
  }

  const { LiveUpdate } = await import("@capawesome/capacitor-live-update");
  const listenerResults = await Promise.allSettled([
    LiveUpdate.addListener("downloadBundleProgress", (event) => {
      onDownloadProgress?.(event);
    }),
    LiveUpdate.addListener("nextBundleSet", (event) => {
      onNextBundleSet?.(event);
    }),
  ]);
  const listenerHandles: Array<Awaited<ReturnType<typeof LiveUpdate.addListener>>> = [];
  let rejectionReason: unknown;

  for (const result of listenerResults) {
    if (result.status === "fulfilled") {
      listenerHandles.push(result.value);
    } else {
      rejectionReason = result.reason;
    }
  }

  if (isDefined(rejectionReason)) {
    await Promise.all(listenerHandles.map((listenerHandle) => listenerHandle.remove()));
    throw toError(rejectionReason);
  }

  return {
    remove: async () => {
      await Promise.all(listenerHandles.map((listenerHandle) => listenerHandle.remove()));
    },
  };
}
